import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Container,
  Typography,
  Chip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useProducts, Product } from '../context/ProductsContext';
import { useWishlist } from '../context/WishlistContext';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useCart } from '../context/CartContext';

const isLowStock = (product: Product, threshold: number) =>
  product.stock != null && product.stock > 0 && product.stock <= threshold;

const Wishlist: React.FC = () => {
  const { ids, toggle } = useWishlist();
  const { addItem } = useCart();
  const { products, settings } = useProducts();

  const favoriteProducts = products.filter((p) => ids.includes(p.id));

  const handleAddToCart = (product: Product) => {
    const finalPrice =
      product.discount && product.discount > 0
        ? product.price * (1 - product.discount / 100)
        : product.price;
    addItem({
      id: product.id,
      name: product.name,
      price: finalPrice,
      quantity: 1,
      image: product.image,
      size: product.sizes && product.sizes.length > 0 ? product.sizes[0] : undefined,
      color: product.colors && product.colors.length > 0 ? product.colors[0] : undefined,
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: 10 }}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 3 }}>
          Mes favoris
        </Typography>

        {favoriteProducts.length === 0 ? (
          <Box textAlign="center" sx={{ py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Vous n'avez encore aucun article en favori.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/products"
              sx={{ mt: 2 }}
            >
              Découvrir les produits
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 4,
            }}
          >
            {favoriteProducts.map((product) => (
              <Card
                key={product.id}
                component={Link}
                to={`/products/${product.id}`}
                sx={{ textDecoration: 'none', color: 'inherit' }}
              >
                <CardMedia
                  component="img"
                  height="260"
                  image={product.image}
                  alt={product.name}
                  loading="lazy"
                />
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    {product.name}
                  </Typography>
                  <Box>
                    {product.discount && product.discount > 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        {product.price.toFixed(2)} €
                      </Typography>
                    )}
                    <Typography variant="h6" color="primary">
                      {(
                        product.discount && product.discount > 0
                          ? product.price * (1 - product.discount / 100)
                          : product.price
                      ).toFixed(2)} €
                    </Typography>
                    {isLowStock(product, settings.lowStockThreshold) && (
                      <Chip
                        label="Stock faible"
                        color="warning"
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddShoppingCartIcon />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddToCart(product);
                    }}
                    disabled={!product.inStock}
                  >
                    {product.inStock ? 'Ajouter au panier' : 'Rupture de stock'}
                  </Button>
                  <Button
                    variant="text"
                    color="secondary"
                    startIcon={<FavoriteIcon />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggle(product.id);
                    }}
                  >
                    Retirer
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Wishlist;
